import { z } from "zod";
import { IP_GUARD_MSG } from "./ipGuard.js";
import { Passkey } from "./passkey.js";

// ---------------------------------------------------------------------------
// Google・Apple でログイン（#112、docs/43 の1回目。2026-10-01 射場さんが §1 の6点を決定）
//   共通の土台（どの製品も同じ）。人（accounts）にログインの手段を足す：パスワード／Google／Apple（パスキーは2回目）。
//   つなぐのは「メールの一致＋相手が確認済みと言う」ときだけ。最後の1本は外せない。
//   画面の文はここに1か所（API のエラーの title も、戻り先の ?oauth_error= の文もここから）
// ---------------------------------------------------------------------------

export const OAuthProvider = z.enum(["google", "apple"]);
export type OAuthProvider = z.infer<typeof OAuthProvider>;
export const OAUTH_PROVIDERS = OAuthProvider.options;

/** login＝ログインの画面／signup＝会員登録の画面（「はじめる」）／link＝設定 → ログイン方法 →「追加」／reauth＝本人確認の窓（#189、docs/76） */
export const OAuthIntent = z.enum(["login", "signup", "link", "reauth"]);
export type OAuthIntent = z.infer<typeof OAuthIntent>;

/** この製品で使える外部ログイン（.env にその製品の鍵があるものだけ true）。画面はボタンを出す／出さないに使う */
export const OAuthAvailability = z.object({ google: z.boolean(), apple: z.boolean() });
export type OAuthAvailability = z.infer<typeof OAuthAvailability>;
export const OAUTH_NONE: OAuthAvailability = { google: false, apple: false };

export const OAUTH_LABEL: Record<OAuthProvider, string> = { google: "Google", apple: "Apple" };

/**
 * ボタンの文（docs/43 §12、2026-10-02 決定）。Apple は Apple の決まりの文（Human Interface Guidelines の日本語。「Apple」と「で」の間に空白を入れない）、
 * Google は日本語の文。login＝ログインの画面と招待の画面（アカウントのある人がログインして参加）、signup＝会員登録の画面
 */
const OAUTH_BUTTON: Record<OAuthProvider, { login: string; signup: string; reauth: string }> = {
  // reauth＝本人確認の窓（#189）。Apple の決まりの「Appleで続ける」
  google: { login: "Google でログイン", signup: "Google で登録", reauth: "Google で続ける" },
  apple: { login: "Appleでサインイン", signup: "Appleで登録", reauth: "Appleで続ける" },
};

export const OAUTH_MSG = {
  /** ログイン・会員登録の区切り */
  or: "または",
  /** ログインの画面・招待の画面のボタン */
  loginWith: (p: OAuthProvider) => OAUTH_BUTTON[p].login,
  /** 会員登録の画面のボタン */
  signupWith: (p: OAuthProvider) => OAUTH_BUTTON[p].signup,
  /** 本人確認の窓のボタン（#189） */
  reauthWith: (p: OAuthProvider) => OAUTH_BUTTON[p].reauth,
  /** 戻ってきたあとの画面（/oauth/continue） */
  continuing: "ログインしています…",
  continueFailed: "ログインできませんでした",
  backToLogin: "ログイン画面へ",

  // ログイン方法（/account/login-methods。#152 から個人設定の中。前の /settings/login-methods は転送）
  title: "ログイン方法",
  password: "パスワード",
  passwordSet: "設定済み",
  passwordUnset: "未設定",
  notLinked: "未追加",
  /** 「yamada@gmail.com ・ 2026/10/01 に追加」 */
  linkedOn: (email: string, date: string) => `${email} ・ ${date} に追加`,
  /** 最後の1本（外すボタンは押せない） */
  onlyMethod: (email: string) => `${email} ・ 唯一のログイン方法`,
  change: "変更",
  add: "追加",
  remove: "解除",
  setPassword: "パスワード設定",
  setPasswordTitle: "パスワード設定",
  setPasswordSubmit: "パスワード設定",
  cancel: "取消",
  linked: (p: OAuthProvider) => `${OAUTH_LABEL[p]} でのログインを追加しました`,
  unlinked: (p: OAuthProvider) => `${OAUTH_LABEL[p]} でのログインを外しました`,
  passwordSaved: "パスワードを設定しました",
  /** 「？」の中（docs/10 §5.1.1：使い方は「？」へ） */
  help: "Google・Apple のアカウントでもログインできます。ログイン方法は1つ以上残してください（最後の1つは外せません）。",

  // 再設定のリンク（パスワードが無い人）
  resetSetTitle: "パスワード設定",

  // 招待（docs/43 §3.5、Figma「確認：招待のメールと違うアカウントでログイン中」）
  inviteOtherTitle: "別のアカウントでログイン中です",
  /** #287：「招待」「参加」をやめた（案内メール・利用開始）。「いまは」→「現在は」 */
  inviteOtherBody: (invited: string, current: string) => `案内メールは ${invited} 宛てです。現在は ${current} でログインしています。`,
  inviteOtherSwitch: "別アカウントでログイン",
  inviteOtherJoin: "このまま利用開始",
} as const;

/**
 * エラーの文（API の title と、戻り先の ?oauth_error=CODE を画面が引く表）。
 * DB が RAISE するもの（AUTH_IDENTITY_* など）は auth.ts の AUTH_ERROR がここを引く
 */
export const OAUTH_ERROR = {
  /** Google が「メールは確認済みでない」と言った（docs/43 §3.2） */
  AUTH_IDENTITY_UNVERIFIED: "メールとパスワードでログインしてから、設定の「ログイン方法」で追加してください",
  AUTH_IDENTITY_TAKEN: "このアカウントは、ほかの人のログインにつながっています",
  AUTH_IDENTITY_EXISTS: "このメールアドレスには別のアカウントがつながっています。設定の「ログイン方法」で外してから追加してください",
  AUTH_IDENTITY_NOT_FOUND: "つながっていません",
  AUTH_LAST_LOGIN_METHOD: "ログインの方法がこれだけなので外せません。先にパスワードを設定してください",
  AUTH_OAUTH_STATE_INVALID: "ログインの確認ができませんでした。もう一度やり直してください",
  AUTH_OAUTH_STATE_EXPIRED: "時間が経ちました。もう一度やり直してください",
  AUTH_OAUTH_EXPIRED: "時間が経ちました。もう一度ログインしてください",
  AUTH_OAUTH_TOKEN_INVALID: "ログインの確認ができませんでした。もう一度やり直してください",
  AUTH_OAUTH_FAILED: "Google・Apple とつながりませんでした。少し待ってからもう一度お試しください",
  AUTH_OAUTH_CANCELLED: "ログインを取りやめました",
  AUTH_OAUTH_NO_EMAIL: "メールアドレスを受け取れませんでした。メールアドレスの共有を許可して、もう一度お試しください",
  AUTH_OAUTH_NOT_CONFIGURED: "この方法ではまだログインできません",
  AUTH_OAUTH_PROVIDER_INVALID: "見つかりません",
  AUTH_STEP_UP_REQUIRED: "確認コードの確認が済んでいません。もう一度「追加」を押してください",
  /** 本人確認（#189）：ログイン中のアカウントにつないでいない Google・Apple で確かめた */
  AUTH_REAUTH_MISMATCH: "ログイン中のアカウントに追加した Google・Apple で確認してください",
  /** 本人確認（#189）：ログイン方法の追加・解除の前に本人確認が済んでいない */
  REAUTH_REQUIRED: "この操作には本人確認が必要です",
  AUTH_REQUIRED: "ログインしてください",
  IP_BLOCKED: IP_GUARD_MSG.blocked,
  IP_SIGNUP_LIMIT: IP_GUARD_MSG.signupLimit,
  TOO_MANY_REQUESTS: "操作が続いたため、いまは受け付けられません。少し時間をおいてからもう一度お試しください",
  PRODUCT_MISMATCH: "このサイトではログインできません。お使いのサービスのサイトを開いてください",
} as const;
export type OAuthErrorCode = keyof typeof OAUTH_ERROR;

/** 戻り先の ?oauth_error= の文。知らない符号は決まった文（URL に書かれた文字をそのまま画面に出さない） */
export function oauthErrorText(code: string | null | undefined): string {
  if (!code) return "";
  return (OAUTH_ERROR as Record<string, string>)[code] ?? "ログインできませんでした。もう一度お試しください";
}

/** 設定 → ログイン方法（GET /account/login-methods） */
export const LoginMethods = z.object({
  email: z.string(),
  password: z.object({ set: z.boolean(), updatedAt: z.string().nullable() }),
  google: z.object({ email: z.string(), linkedAt: z.string() }).nullable(),
  apple: z.object({ email: z.string(), linkedAt: z.string() }).nullable(),
  /** パスキー（#113）。この製品のドメインのものだけ（ほかの製品の鍵はここでは使えない） */
  passkeys: z.array(Passkey),
  /** この製品で使えるログインの方法の数（パスワード＋Google＋Apple＋この製品のパスキー）。1なら外すボタンは押せない */
  count: z.number().int(),
  /** この製品で追加できる外部（鍵の無いものは「追加」を出さない） */
  available: OAuthAvailability,
});
export type LoginMethods = z.infer<typeof LoginMethods>;

/** POST /account/identities/:provider/start → この URL へ移る（確認コード〔海外〕はこの POST で済ませる） */
export const OAuthLinkStart = z.object({ url: z.string() });
export type OAuthLinkStart = z.infer<typeof OAuthLinkStart>;

/** 戻り先にできるパス（このサイトの中だけ。クエリは持たない＝招待の token などを外へ持ち出さない） */
export const OAUTH_RETURN_RE = /^\/[A-Za-z0-9/_-]{0,100}$/;
